import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Activity, Zap, Shield, TrendingUp, Users, Play, Heart, Star } from 'lucide-react';

export default function Home() {
  const stats = [
    { value: '500+', label: 'Healthcare Partners', icon: Users },
    { value: '99.7%', label: 'AI Accuracy Rate', icon: TrendingUp },
    { value: '2M+', label: 'Patients Analyzed', icon: Heart },
    { value: '50ms', label: 'Response Time', icon: Zap },
  ];

  const features = [
    {
      icon: Brain,
      title: 'Advanced AI',
      description: 'State-of-the-art machine learning algorithms trained on vast medical datasets for unprecedented accuracy.',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Shield,
      title: 'HIPAA Compliant',
      description: 'Enterprise-grade security ensuring your data is always protected with 256-bit encryption.',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: TrendingUp,
      title: 'Proven Results',
      description: 'Demonstrated 40% improvement in diagnostic accuracy and operational efficiency.',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Zap,
      title: 'Real-Time Analysis',
      description: 'Instant insights in under 50ms that accelerate decision-making and improve patient care.',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Dedicated team of medical and technical experts available 24/7 for your success.',
      color: 'from-pink-500 to-rose-600'
    },
    {
      icon: Activity,
      title: 'Seamless Integration',
      description: 'Works with your existing EMR systems and workflows without any disruption.',
      color: 'from-blue-500 to-cyan-600'
    }
  ];

  const testimonials = [
    {
      quote: "PulseNex's AI analytics have transformed how we approach preventive cardiology. The predictive accuracy is remarkable.",
      author: "Dr. Sarah Chen",
      role: "Chief of Cardiology",
      org: "Northwestern Memorial Hospital",
      initials: "SC"
    },
    {
      quote: "Implementation was seamless, and the ROI was evident within the first quarter. Our diagnostic efficiency improved by 40%.",
      author: "Dr. Michael Roberts",
      role: "Medical Director",
      org: "Cleveland Clinic",
      initials: "MR"
    },
    {
      quote: "The most sophisticated yet user-friendly medical AI platform we've encountered. It's become indispensable to our practice.",
      author: "Dr. Emily Watson",
      role: "Head of Digital Health",
      org: "Mayo Clinic",
      initials: "EW"
    }
  ];

  const trustedBy = ['Mayo Clinic', 'Cleveland Clinic', 'Johns Hopkins', 'Mass General', 'Stanford Health', 'UCLA Health'];

  return (
    <div className="min-h-screen bg-[#030712]">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-20 lg:pt-44 lg:pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
              <span className="text-cyan-400 text-sm font-medium">HIPAA Compliant</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Transforming Healthcare with{' '}
              <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
                Artificial Intelligence
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-400 mb-10 leading-relaxed max-w-2xl mx-auto">
              PulseNex AI delivers enterprise-grade predictive analytics and intelligent
              automation solutions that empower healthcare providers to save more lives.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/products"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-cyan-500/25 transition-all hover:-translate-y-1"
              >
                Explore Products
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/5 border border-white/10 text-white font-semibold rounded-xl hover:bg-white/10 transition-all"
              >
                <Play className="mr-2 w-5 h-5" />
                Watch Overview
              </Link>
            </div>

            {/* Trust Logos */}
            <div className="pt-12 border-t border-white/5">
              <p className="text-gray-500 text-sm mb-6">Trusted by leading healthcare institutions</p>
              <div className="flex flex-wrap items-center justify-center gap-8 opacity-50">
                {trustedBy.map((name) => (
                  <span key={name} className="text-gray-400 font-medium text-sm">{name}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-600/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-cyan-500/30 transition-all">
                  <stat.icon className="w-8 h-8 text-cyan-400 mb-4" />
                  <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-gray-500 mt-1 text-sm">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
              <Zap className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 text-sm font-medium">Why Choose PulseNex</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
              Built for Modern Healthcare
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              We combine medical expertise with advanced AI technology to deliver innovative healthcare solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="group relative">
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} rounded-2xl opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-500`} />
                <div className="relative h-full bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all">
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.color} mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative z-10 py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full mb-6">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-medium">Testimonials</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
              Trusted by Healthcare Leaders
            </h2>
            <p className="text-gray-400 text-lg">
              See what medical professionals are saying about PulseNex AI
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded-3xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity" />
                <div className="relative h-full bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-3xl p-8 hover:border-white/20 transition-all">
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-8 leading-relaxed">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center font-bold text-white">
                      {testimonial.initials}
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{testimonial.author}</h4>
                      <p className="text-gray-400 text-sm">{testimonial.role}</p>
                      <p className="text-cyan-400 text-sm">{testimonial.org}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-blue-600 to-purple-600" />
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}
            />
            <div className="relative px-8 py-16 lg:px-16 lg:py-24 text-center">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
                Ready to Transform Your Healthcare Practice?
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto mb-10">
                Join hundreds of healthcare organizations using PulseNex AI to deliver better patient outcomes and operational efficiency.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all hover:-translate-y-1 flex items-center gap-2"
                >
                  Schedule a Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-white/10 border border-white/20 rounded-xl font-semibold text-lg text-white hover:bg-white/20 transition-all"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
